import {
  Component,
  OnInit,
  OnDestroy,
  ElementRef,
  ViewChild,
} from '@angular/core';
import { Subject } from 'rxjs';
import { finalize, takeUntil } from 'rxjs/operators';
import { FormControl } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { IrisService } from '../../../core/services/iris.service';
import { NotificationService } from '../../../core/services/notification.service';
import { ItfgDataTableColumn } from '@app/core/components/data-table/types/data-table.column';
import {
  IrisUserActivity,
  IrisUserActivityDetail,
  IrisActivityFiltersDTO,
  IrisTimelineEvent,
  IrisTimelineEventType,
  IrisTimelineGroup,
  IrisDeactivationReason,
  SortDirection,
  UserActivitySortField,
  ActivityPeriod,
} from '../../../core/types/iris-report';

import { HttpErrorResponse } from '@angular/common/http';
import { Administrator } from '@app/core/types/administrator';
import { AdministratorService } from '@app/core/services/administrator.service';
import { AdministratorEmailToDisplayTextPipe } from '../pipes/administrator-email-to-display-text.pipe';
import { StepTitles } from '@app/core/components/timeline/components/timeline.component';
import {
  BankConsentTimelineStyleConfigMap,
  BANK_CONSENT_TIMELINE_EVENT_STYLE_MAP,
} from './bank-consent-timeline-event-icon';
import { ItfgDateFormatPipe } from '@app/shared/pipes/itfg-date-format.pipe';
import { DateFormat } from '@app/core/types/date-format';
import { ItfgDataTableSortChangeEvent } from '@app/core/components/data-table/types/sort.type';

@Component({
  selector: 'bank-consents-activity',
  templateUrl: './bank-consents-activity.component.html',
  styleUrls: ['./bank-consents-activity.component.scss'],
  standalone: false,
})
export class BankConsentsActivityComponent implements OnInit, OnDestroy {
  columns: ItfgDataTableColumn[];
  users: IrisUserActivity[] = [];
  administrators: Administrator[];
  selectedUser: IrisUserActivity | null = null;
  userDetail: IrisUserActivityDetail | null = null;
  timelineGroups: IrisTimelineGroup[] = [];
  timelineSteps: StepTitles[] = [];
  listLoading: boolean = false;
  detailLoading: boolean = false;
  loadingMore: boolean = false;
  allLoaded: boolean = false;
  expandedEvents = new Set<unknown>();

  private readonly PAGE_SIZE = 50;
  private offset = 0;

  // Filter controls
  filterCivilId = new FormControl('');
  filterName = new FormControl('');
  filterEmail = new FormControl('');
  filterPeriod = new FormControl<ActivityPeriod>(ActivityPeriod.OneDay, {
    nonNullable: true,
  });

  ActivityPeriod = ActivityPeriod;
  periodOptions: ActivityPeriod[] = [
    ActivityPeriod.OneDay,
    ActivityPeriod.SevenDays,
    ActivityPeriod.ThirtyDays,
  ];

  private currentSort: {
    sortBy: UserActivitySortField;
    direction: SortDirection;
  } = {
    sortBy: UserActivitySortField.LastRequestAt,
    direction: SortDirection.Desc,
  };

  private readonly SORT_FIELD_MAP: Record<string, UserActivitySortField> = {
    lastRequestAt: UserActivitySortField.LastRequestAt,
    requestsCountForPeriod: UserActivitySortField.RequestsForPeriod,
    consentsCountForPeriod: UserActivitySortField.ConsentsForPeriod,
  };

  TimelineEventType: typeof IrisTimelineEventType = IrisTimelineEventType;
  DeactivationReason: typeof IrisDeactivationReason = IrisDeactivationReason;
  timelineStyleMap: BankConsentTimelineStyleConfigMap =
    BANK_CONSENT_TIMELINE_EVENT_STYLE_MAP;

  private _unsubscribe = new Subject<void>();

  @ViewChild('tableScrollContainer') tableScrollContainer: ElementRef;

  constructor(
    private irisService: IrisService,
    private notificationService: NotificationService,
    private translate: TranslateService,
    private administratorService: AdministratorService,
    private administratorEmailToDisplayText: AdministratorEmailToDisplayTextPipe,
    private datePipe: ItfgDateFormatPipe
  ) {}

  ngOnInit(): void {
    this.administratorService.administrators$
      .pipe(takeUntil(this._unsubscribe))
      .subscribe((administrators: Administrator[]) => {
        this.administrators = administrators;
      });

    const columnLabels$ = this.getColumnLabels();

    columnLabels$.subscribe((translations) => {
      this.setupColumnConfig(translations);
    });

    this.loadUsers();
  }

  getColumnLabels() {
    return this.translate.get([
      'global.brand',
      'global.client',
      'global.civilId',
      'bankInfo.activity.columns.requests',
      'bankInfo.activity.columns.lastActive',
      'bankInfo.activity.columns.activeConsents',
    ]);
  }

  setupColumnConfig(translations: { [key: string]: string }): void {
    this.columns = [
      {
        name: 'brand',
        label: translations['global.brand'],
        width: 50,
        isSortable: false,
      },
      {
        name: 'fullName',
        label: translations['global.client'],
        width: { min: 150 },
        isSortable: false,
      },
      {
        name: 'civilId',
        label: translations['global.civilId'],
        width: 100,
        isSortable: false,
      },
      {
        name: 'lastRequestAt',
        label: translations['bankInfo.activity.columns.lastActive'],
        width: 150,
        isSortable: true,
      },
      {
        name: 'requestsCountForPeriod',
        label: translations['bankInfo.activity.columns.requests'],
        width: 120,
        isSortable: true,
      },

      {
        name: 'consentsCountForPeriod',
        label: translations['bankInfo.activity.columns.activeConsents'],
        width: 130,
        isSortable: true,
      },
    ];
  }

  onSortChange(event: ItfgDataTableSortChangeEvent): void {
    const sortBy = this.SORT_FIELD_MAP[event.name];

    if (!sortBy) {
      return;
    }

    // empty string = sort cleared by user; keep field, default to DESC
    const direction =
      event.order === 'ASC' ? SortDirection.Asc : SortDirection.Desc;

    this.currentSort = { sortBy, direction };
    this.resetAndLoad();
  }

  loadUsers(append = false): void {
    if (!append) {
      this.listLoading = true;
    } else {
      this.loadingMore = true;
    }

    const filters: IrisActivityFiltersDTO = {
      limit: this.PAGE_SIZE,
      offset: this.offset,
      sortBy: this.currentSort.sortBy,
      direction: this.currentSort.direction,
      period: this.filterPeriod.value,
    };

    const civilId = this.filterCivilId.value?.trim();
    const name = this.filterName.value?.trim();
    const email = this.filterEmail.value?.trim();

    if (civilId) filters.civilId = civilId;
    if (name) filters.name = name;
    if (email) filters.email = email;
    filters.period = this.filterPeriod.value;

    this.irisService
      .getActivityUsers(filters)
      .pipe(
        takeUntil(this._unsubscribe),
        finalize(() => {
          this.listLoading = false;
          this.loadingMore = false;
        })
      )
      .subscribe({
        next: (result: IrisUserActivity[]) => {
          if (append) {
            this.users = [...this.users, ...result];
          } else {
            this.users = result;
          }

          if (result.length < this.PAGE_SIZE) {
            this.allLoaded = true;
          }
        },
        error: (err: HttpErrorResponse) => {
          this.notificationService.showLocalizedErrorMessage({
            notificationText: this.translate.instant(
              'bankInfo.activity.fetchListError'
            ),
          });
        },
      });
  }

  resetAndLoad(): void {
    this.offset = 0;
    this.allLoaded = false;
    // this.users = [];
    this.loadUsers(false);
  }

  clearFilters(field: string): void {
    if (field === 'civilId') {
      this.filterCivilId.setValue('');
    }

    if (field === 'name') {
      this.filterName.setValue('');
    }

    if (field === 'email') {
      this.filterEmail.setValue('');
    }

    this.resetAndLoad();
  }

  onTableScroll(event: Event): void {
    if (this.loadingMore || this.allLoaded) {
      return;
    }

    const el = event.target as HTMLElement;
    const threshold = 100;
    const reachedBottom =
      el.scrollHeight - el.scrollTop - el.clientHeight < threshold;

    if (reachedBottom) {
      this.offset += this.PAGE_SIZE;
      this.loadUsers(true);
    }
  }

  onRowClick(user: IrisUserActivity): void {
    this.selectedUser = user;
    this.userDetail = null;
    this.timelineGroups = [];
    this.timelineSteps = [];
    this.detailLoading = true;

    this.irisService
      .getActivityUser(user.id)
      .pipe(
        takeUntil(this._unsubscribe),
        finalize(() => {
          this.detailLoading = false;
        })
      )
      .subscribe({
        next: (detail: IrisUserActivityDetail) => {
          this.userDetail = detail;
          this.timelineGroups = this.groupTimelineByDate(detail.timeline);
          this.timelineSteps = this.buildTimelineSteps(this.timelineGroups);
        },
        error: (err: HttpErrorResponse) => {
          this.notificationService.showLocalizedErrorMessage({
            notificationText: this.translate.instant(
              'bankInfo.activity.fetchDetailError'
            ),
          });
        },
      });
  }

  groupTimelineByDate(events: IrisTimelineEvent[]): IrisTimelineGroup[] {
    const sorted = [...events].sort(
      (a, b) =>
        new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
    );

    const groups: Map<string, IrisTimelineEvent[]> = new Map();

    for (const event of sorted) {
      const dateKey = event.createdAt.substring(0, 10);
      if (!groups.has(dateKey)) {
        groups.set(dateKey, []);
      }
      groups.get(dateKey)!.push(event);
    }

    return Array.from(groups.entries()).map(([date, evts]) => ({
      date,
      events: evts,
    }));
  }

  buildTimelineSteps(groups: IrisTimelineGroup[]): StepTitles[] {
    return groups.map((group) => {
      let titleParts = [
        this.datePipe.transform(group.date, DateFormat.DATE),
        `${group.events.length} ${
          group.events.length === 1
            ? this.translate.instant('bankInfo.activity.timeline.event')
            : this.translate.instant('bankInfo.activity.timeline.events')
        }`,
      ];

      return {
        title: titleParts.join(' · '),
        data: group.events,
      };
    });
  }

  openClientCredit(): void {
    if (!this.selectedUser?.civilId || !this.selectedUser?.brand) return;

    const brand = this.selectedUser.brand;
    const civilId = this.selectedUser.civilId;

    window.open(
      `/#/credits/${brand}/${civilId}/latest?tab=incomesTab&tabView=BANK_INFO`,
      '_blank'
    );
  }

  getDeactivatedByDisplay(operatorId: string): string {
    const admin = this.findMatchingOperator(operatorId);
    if (admin) {
      return this.administratorEmailToDisplayText.transform(admin.email);
    }
    return `#${operatorId}`;
  }

  findMatchingOperator(operatorId: number | string): Administrator | null {
    return this.administrators.find(
      (administrator: Administrator) => administrator.id === Number(operatorId)
    );
  }

  isSelected(user: IrisUserActivity): boolean {
    return this.selectedUser?.id === user.id;
  }

  toggleEvent(event: unknown): void {
    if (this.expandedEvents.has(event)) {
      this.expandedEvents.delete(event);
    } else {
      this.expandedEvents.add(event);
    }
  }

  isExpanded(event: unknown): boolean {
    return this.expandedEvents.has(event);
  }

  hasDetails(event: any): boolean {
    return !!(
      event.iban ||
      event.product ||
      event.ownerName ||
      event.errorMessage ||
      event.type === 'CONSENT_DEACTIVATED' // check type!!
    );
  }

  ngOnDestroy(): void {
    this._unsubscribe.next();
    this._unsubscribe.complete();
  }
}
